import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const BASKET_IMAGES = {
  hero: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
    alt: "Precision fabricated stainless steel wire mesh filter basket and flat filter plate assembly",
  },
  overview: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
    alt: "Stainless steel filter basket with reinforced perforated support core and welded lifting handle",
  },
  parent: {
    src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
    alt: "Stainless steel filter components and precision filtration assemblies",
  },
  discs: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
    alt: "Precision stamped stainless steel filter discs and slit coils",
  },
  tubes: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
    alt: "Welded stainless steel wire mesh filter tubes and cylinders",
  },
  cartridges: {
    src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
    alt: "Pleated and cylindrical stainless steel filter cartridges",
  },
  screenPacks: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
    alt: "Spot-welded and framed multilayer extruder screen packs",
  },
};

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "FilterBasketsAndPlates",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Filter Components",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Filter Baskets and Plates",
      lead: "Engineered stainless steel strainer baskets and rigid leaf filter plates for high-volume liquid clarification, pipeline straining, and pressure filtration. Built with heavy calendered mesh, sintered laminate plates, and reinforced perforated-sheet support cores.",
      secondaryCta: {
        label: "Filter Components Overview",
        to: "/FilterComponentsPage",
      },
      image: BASKET_IMAGES.hero,
      cardNote: "Perforated core & mesh composite",
      cardLink: {
        label: "Explore Specifications",
        to: "#specifications",
      },
      specs: [
        { label: "Aperture Range", value: "1 µm – 25 mm" },
        { label: "Plate Formats", value: "Up to 2,500 × 1,600 mm" },
        { label: "Support Core", value: "Perforated 304 / 316" },
        { label: "Standard Alloys", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Structural Rigidity & Separation Control",
      heading: "Engineered for High-Capacity Fluid Clarification and Leaf Filtration",
      paragraphs: [
        "Filter baskets and flat filter plates serve as vital process elements across pipeline strainers, industrial clarifying vessels, pressure leaf filters, and plate-and-frame dewatering systems. Each unit combines an outer or inner protective mesh layer with a heavier structural substrate, preventing deformation under high mechanical fluid loads.",
        "Fabricated configurations accommodate continuous liquid straining, solid particulate recovery, and slurry dewatering. We supply basket designs with custom slant collars, flat or dished bottoms, precision machined flange rings, and integrated lifting handles for ergonomic maintenance.",
        "Flat filter plates and leaves are engineered with solid edge welds, CNC-profiled perimeter frames, or reinforced bolted flanges. Multiple mesh layers can be combined—utilizing Dutch weave or square weave cloth over perforated backing sheets—to balance flow volume with precise particulate retention.",
      ],
      keyPoints: [
        "Heavy perforated plate backing and structural framing resist mechanical pressure loads without media distortion.",
        "Custom vessel-ready basket geometry including slanted collars, flanged rims, bayonet locators, and lifting handles.",
        "Rigid filter plate formats available with fully seal-welded solid edges, perimeter bolt holes, and elastomer gasket grooves.",
      ],
      image: BASKET_IMAGES.overview,
    },
    specTable: {
      heading: "Manufacturing Parameters & Fabrication Specifications",
      intro: "Standard dimensional ranges and construction formats for fabricated basket strainers and rigid filter plates.",
      columns: [
        { key: "parameter", label: "Parameter / Component", align: "left" },
        { key: "filterBaskets", label: "Strainer Baskets", align: "left" },
        { key: "filterPlates", label: "Filter Plates & Leaves", align: "left" },
        { key: "standardOptions", label: "Standard Options", align: "left" },
      ],
      rows: [
        {
          parameter: "Filtration Rating",
          filterBaskets: "20 µm – 25 mm",
          filterPlates: "1 µm – 500 µm",
          standardOptions: "Dutch weave, square weave, sintered plate",
        },
        {
          parameter: "Primary Material",
          filterBaskets: "AISI 304, AISI 316, 316L",
          filterPlates: "AISI 304, AISI 316, 316L",
          standardOptions: "MTR available when specified in the order",
        },
        {
          parameter: "Support Substrate",
          filterBaskets: "Perforated sheet (round/staggered)",
          filterPlates: "Perforated core / coarse drainage grid",
          standardOptions: "1.0 mm – 4.0 mm plate thickness",
        },
        {
          parameter: "Max Dimensions",
          filterBaskets: "Diameters up to 900 mm, depths to 1,500 mm",
          filterPlates: "Up to 2,500 × 1,600 mm sheet format",
          standardOptions: "Custom vessel dimensions to order drawing",
        },
        {
          parameter: "Edge / Rim Treatment",
          filterBaskets: "Solid collar, machined flange, wire ring",
          filterPlates: "TIG-welded edge, U-binder, bolted frame",
          standardOptions: "O-ring grooves, lifting bails, mounting tabs",
        },
      ],
      caption: "Values represent verified engineering ranges. Dimensional tolerances follow project drawings and order requirements.",
    },
    features: {
      heading: "Engineering Strengths & Assembly Methods",
      intro: "Constructed to withstand demanding process conditions while facilitating efficient maintenance and long service life.",
      items: [
        {
          title: "Reinforced Support Core",
          text: "Heavy perforated stainless steel inner or outer cylinders absorb process fluid pressure, preventing wire cloth deformation during surging or cake buildup.",
        },
        {
          title: "Precision Boundary Sealing",
          text: "Machined flange rims, laser-cut collar rings, and continuous TIG edge welds eliminate bypass around strainer housings and filter press chambers.",
        },
        {
          title: "Multiple Media Combinations",
          text: "Single, dual, or multi-layer mesh constructions—including plain weave, twilled Dutch weave, and sintered laminates—tailored to liquid viscosity.",
        },
        {
          title: "Ergonomic Handling",
          text: "Integrated swing bails, rigid cross-handles, or threaded extraction points allow fast removal and pressure cleaning during turnaround cycles.",
        },
      ],
    },
    related: {
      heading: "Related Filter Components & Parent Family",
      intro: "Explore companion fabricated filtration assemblies engineered for industrial straining, separation, and component mounting.",
      cards: [
        {
          title: "Filter Components Hub",
          text: "Explore all fabricated components including discs, cylinders, cartridges, packs, and vessel assemblies.",
          to: "/FilterComponentsPage",
          image: BASKET_IMAGES.parent,
          badge: "Parent Category",
          linkLabel: "View Category Overview",
        },
        {
          title: "Filter Discs & Slit Coils",
          text: "Single and multi-layer stamped discs, circular mesh packs, and precision slit coil strips.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: BASKET_IMAGES.discs,
          badge: "Sibling Component",
          linkLabel: "Explore Filter Discs",
        },
        {
          title: "Filter Tubes & Cylinders",
          text: "Rigid longitudinal seam-welded and spirally wound wire mesh tubes and protective filter sleeves.",
          to: "/FilterTubesAndCylindersPage",
          image: BASKET_IMAGES.tubes,
          badge: "Sibling Component",
          linkLabel: "Explore Filter Tubes",
        },
        {
          title: "Filter Cartridges",
          text: "Pleated and cylindrical industrial filter elements engineered for high dirt-holding capacity.",
          to: "/FilterCartridgesPage",
          image: BASKET_IMAGES.cartridges,
          badge: "Sibling Component",
          linkLabel: "Explore Cartridges",
        },
        {
          title: "Screen Packs",
          text: "Multilayer spot-welded, rimmed, and bordered screen packs for polymer melt and extrusion filtration.",
          to: "/ScreenPacksPage",
          image: BASKET_IMAGES.screenPacks,
          badge: "Sibling Component",
          linkLabel: "Explore Screen Packs",
        },
      ],
    },
    cta: {
      id: "cta-filter-baskets",
      eyebrow: "Custom Basket & Plate Fabrication",
      heading: "Need Custom Dimensions for Your Vessel or Filter Press?",
      lead: "Provide your vessel dimensions, mesh rating, media layering, and rim geometry for custom-fabricated baskets and plates.",
      secondaryLabel: "Contact Engineering",
      secondaryTo: "/ContactPage",
      note: "Drawings in DWG, DXF, or PDF format accepted for custom quotes.",
    },
  },
  zh: {
    breadcrumbKey: "FilterBasketsAndPlates",
    relatedTopic: "components",
    hero: {
      categoryBadge: "过滤组件",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "不锈钢滤篮与平板滤板",
      lead: "专为高通量液体澄清、管道拦污及工业叶片压滤设计的精密不锈钢篮式滤筒与平整滤板。采用重型压延编织网、烧结多层网板以及加固冲孔板支撑内胆一体焊接而成。",
      secondaryCta: {
        label: "过滤组件概览",
        to: "/FilterComponentsPage",
      },
      image: BASKET_IMAGES.hero,
      cardNote: "冲孔内胆与丝网复合结构",
      cardLink: {
        label: "查看技术参数",
        to: "#specifications",
      },
      specs: [
        { label: "孔径 / 精度", value: "1 µm – 25 mm" },
        { label: "平板规格", value: "达 2,500 × 1,600 mm" },
        { label: "支撑骨架", value: "304 / 316 冲孔板" },
        { label: "标准材质", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "结构刚性与流体澄清",
      heading: "专为大通量流体拦污与压滤工艺研发的加固构件",
      paragraphs: [
        "过滤篮与平板滤板是管道过滤器、流体澄清槽、叶片压滤机及板框压滤机中的核心承载构件。每个构件将精细滤网与高强度冲孔或格栅支撑板结合，抵御高压差及机械冲击，保持长期平整稳定。",
        "产品支持根据工艺介质定制：滤篮具备平底、锥底、斜口法兰以及快拆吊环提手等构型，便于安装在各类管道篮式过滤器壳体内进行周期性清洗与更换。",
        "平板滤板与滤叶边缘采用全焊密封、CNC 精密加工包边或螺栓紧固法兰，支持单层粗滤网、双层复合网以及平纹/斜纹荷兰编织网，在保障大流通面积的同时满足精密分离需求。",
      ],
      keyPoints: [
        "重型冲孔支撑板骨架与加固边框结合，承受高机械压力与滤饼积聚不发生变形。",
        "按壳体图纸定制斜口、平底、法兰定位圈、快装卡扣及人体工学提取把手。",
        "平板过滤件提供连续熔焊密封边缘、螺栓连接法兰及精密密封圈压槽。",
      ],
      image: BASKET_IMAGES.overview,
    },
    specTable: {
      heading: "制造参数与结构规格",
      intro: "篮式过滤器滤芯与工业过滤平板的标准工艺范围与结构选型。",
      columns: [
        { key: "parameter", label: "参数 / 构件项目", align: "left" },
        { key: "filterBaskets", label: "篮式滤芯 / 滤篮", align: "left" },
        { key: "filterPlates", label: "过滤平板 / 滤叶", align: "left" },
        { key: "standardOptions", label: "工艺与标准选项", align: "left" },
      ],
      rows: [
        {
          parameter: "过滤精度范围",
          filterBaskets: "20 µm – 25 mm",
          filterPlates: "1 µm – 500 µm",
          standardOptions: "荷兰编织网、方孔平织网、烧结板",
        },
        {
          parameter: "主体合金材质",
          filterBaskets: "AISI 304, AISI 316, 316L",
          filterPlates: "AISI 304, AISI 316, 316L",
          standardOptions: "按订单要求可提供材质检验报告 (MTR)",
        },
        {
          parameter: "支撑结构",
          filterBaskets: "冲孔金属板（错列圆孔）",
          filterPlates: "冲孔板芯 / 粗导流金属支撑格栅",
          standardOptions: "板厚 1.0 mm – 4.0 mm 可选",
        },
        {
          parameter: "外形尺寸极限",
          filterBaskets: "直径达 900 mm，高度达 1,500 mm",
          filterPlates: "平板幅面达 2,500 × 1,600 mm",
          standardOptions: "按容器图纸严格定制外形尺寸",
        },
        {
          parameter: "边缘与口圈构造",
          filterBaskets: "实心法兰环、车削密封圈、圆钢压圈",
          filterPlates: "TIG 满焊包边、U型金属边条、螺栓框",
          standardOptions: "可加工 O 型密封圈槽、定位耳、吊耳",
        },
      ],
      caption: "数据代表标准工程范围。具体公差与接合方式依据客户工程图纸及订单要求执行。",
    },
    features: {
      heading: "工艺优势与装配特性",
      intro: "结构坚固耐用，兼顾严苛工艺耐久性与日常维护便利性。",
      items: [
        {
          title: "加固冲孔支撑内胆",
          text: "厚壁不锈钢冲孔骨架吸收管线流体冲击与压差载荷，有效防止过滤网布失稳破损。",
        },
        {
          title: "精密贴合无旁漏设计",
          text: "CNC 加工法兰圈与密闭包边焊接工艺，确保滤篮与壳体密封面紧密贴合，杜绝未过滤颗粒旁漏。",
        },
        {
          title: "多层复合介质组合",
          text: "可搭配方孔编织网、荷兰编织高流速网或多层烧结网板，兼顾高通量与微米级精度截留。",
        },
        {
          title: "便捷清洗与维护把手",
          text: "集成弧形活动提手、十字横梁或顶部螺纹起吊孔，方便在检修停机时快速取出反冲洗。",
        },
      ],
    },
    related: {
      heading: "相关过滤组件与母分类",
      intro: "浏览其他精密制造的不锈钢过滤元件，满足工业管线拦截、分离与成型装配需求。",
      cards: [
        {
          title: "过滤组件产品中心",
          text: "查看滤网片、滤筒、滤芯、滤网组及篮式构件等完整精密加工品类。",
          to: "/FilterComponentsPage",
          image: BASKET_IMAGES.parent,
          badge: "母分类",
          linkLabel: "查看分类概览",
        },
        {
          title: "滤网片与分条网带",
          text: "单层及多层冲压圆形、异形不锈钢过滤网片与高精度连续分条网卷。",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: BASKET_IMAGES.discs,
          badge: "同系组件",
          linkLabel: "探索过滤网片",
        },
        {
          title: "过滤管与圆柱滤筒",
          text: "精密纵缝焊接、螺旋卷焊不锈钢金属丝网圆柱滤筒与支撑保护套管。",
          to: "/FilterTubesAndCylindersPage",
          image: BASKET_IMAGES.tubes,
          badge: "同系组件",
          linkLabel: "探索圆柱滤管",
        },
        {
          title: "工业滤芯",
          text: "高容污折叠滤芯与多层圆柱滤芯，专为严苛流体工况提供大有效过滤面积。",
          to: "/FilterCartridgesPage",
          image: BASKET_IMAGES.cartridges,
          badge: "同系组件",
          linkLabel: "探索工业滤芯",
        },
        {
          title: "挤出机过滤网包",
          text: "多层点焊、金属包边及异形冲压滤网组，专为聚合物挤出熔体过滤制造。",
          to: "/ScreenPacksPage",
          image: BASKET_IMAGES.screenPacks,
          badge: "同系组件",
          linkLabel: "探索挤出网包",
        },
      ],
    },
    cta: {
      id: "cta-filter-baskets",
      eyebrow: "非标滤篮与滤板定制",
      heading: "需要匹配您现有容器或滤机的专属规格？",
      lead: "请提供您的过滤器壳体尺寸、精度要求、介质构成及法兰图纸，我们将为您提供制造方案与报价。",
      secondaryLabel: "联系技术支持",
      secondaryTo: "/ContactPage",
      note: "支持接收 DWG、DXF、STEP 或 PDF 格式图纸进行定制评审。",
    },
  },
  es: {
    breadcrumbKey: "FilterBasketsAndPlates",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Componentes Filtrantes",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Cestas y Placas Filtrantes",
      lead: "Filtros de cesta y placas planas de acero inoxidable fabricados con precisión para clarificación de fluidos de gran volumen, retención en tuberías y filtración a presión. Ensamblados con malla calandrada pesada, placas sinterizadas y núcleos de chapa perforada reforzada.",
      secondaryCta: {
        label: "Resumen de Componentes",
        to: "/FilterComponentsPage",
      },
      image: BASKET_IMAGES.hero,
      cardNote: "Compuesto de núcleo perforado y malla",
      cardLink: {
        label: "Ver Especificaciones",
        to: "#specifications",
      },
      specs: [
        { label: "Rango de Apertura", value: "1 µm – 25 mm" },
        { label: "Formato de Placas", value: "Hasta 2.500 × 1.600 mm" },
        { label: "Núcleo Soporte", value: "Perforado 304 / 316" },
        { label: "Aleaciones Estándar", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Rigidez Estructural y Clarificación",
      heading: "Diseñadas para Filtración a Presión y Retención de Gran Caudal",
      paragraphs: [
        "Las cestas filtrantes y las placas planas son elementos fundamentales en filtros de tubería, recipientes clarificadores, filtros de hojas a presión y prensas de deshidratación. Cada unidad combina capas de malla de alta precisión con un sustrato perforado de soporte para resistir elevadas presiones mecánicas.",
        "Las configuraciones fabricadas se adaptan a la separación continua de sólidos y a la recuperación de lodos. Suministramos diseños con bocas inclinadas, fondos planos o cónicos, aros de brida mecanizados y asas de elevación ergonómicas.",
        "Las placas planas cuentan con soldadura periférica continua, marcos mecanizados o bridas atornilladas de refuerzo. Es posible combinar tejidos lisos, holandeses o placas laminadas sinterizadas para conciliar caudal y precisión de retención.",
      ],
      keyPoints: [
        "Soporte de chapa perforada gruesa y marco estructural para soportar cargas mecánicas sin deformación.",
        "Geometría adaptada al recipiente con bocas inclinadas, aros con brida y asas de elevación rápida.",
        "Placas rígidas disponibles con bordes soldados estancos, perforaciones de brida y canales para juntas.",
      ],
      image: BASKET_IMAGES.overview,
    },
    specTable: {
      heading: "Parámetros de Fabricación y Especificaciones",
      intro: "Rangos dimensionales habituales y variantes de construcción para cestas y placas filtrantes.",
      columns: [
        { key: "parameter", label: "Parámetro / Elemento", align: "left" },
        { key: "filterBaskets", label: "Cestas Filtrantes", align: "left" },
        { key: "filterPlates", label: "Placas y Hojas Filtrantes", align: "left" },
        { key: "standardOptions", label: "Opciones Estándar", align: "left" },
      ],
      rows: [
        {
          parameter: "Rango de Filtración",
          filterBaskets: "20 µm – 25 mm",
          filterPlates: "1 µm – 500 µm",
          standardOptions: "Tejido holandés, tejido liso cuadrado, placa sinterizada",
        },
        {
          parameter: "Materiales",
          filterBaskets: "AISI 304, AISI 316, 316L",
          filterPlates: "AISI 304, AISI 316, 316L",
          standardOptions: "Certificado MTR disponible según pedido",
        },
        {
          parameter: "Sustrato de Soporte",
          filterBaskets: "Chapa perforada (orificios redondos)",
          filterPlates: "Núcleo perforado / rejilla de drenaje",
          standardOptions: "Espesor de chapa de 1,0 mm a 4,0 mm",
        },
        {
          parameter: "Dimensiones Máximas",
          filterBaskets: "Diámetros hasta 900 mm, altura hasta 1.500 mm",
          filterPlates: "Formatos hasta 2.500 × 1.600 mm",
          standardOptions: "Fabricación a medida según plano",
        },
        {
          parameter: "Acabado de Bordes",
          filterBaskets: "Brida sólida mecanizada, anillo de alambre",
          filterPlates: "Soldadura perimetral TIG, perfil en U",
          standardOptions: "Canal para junta tórica, asas, orejetas",
        },
      ],
      caption: "Valores representativos. Tolerancias dimensionales según plano de ingeniería y requisitos del pedido.",
    },
    features: {
      heading: "Puntos Fuertes de Ingeniería y Montaje",
      intro: "Fabricación resistente diseñada para facilitar la limpieza rutinaria y prolongar la vida útil.",
      items: [
        {
          title: "Núcleo de Soporte Reforzado",
          text: "Chapa perforada de gran espesor que absorbe el empuje dinámico del fluido, evitando fallos en la malla fina.",
        },
        {
          title: "Sellado sin Fugas",
          text: "Bridas mecanizadas y uniones soldadas estancas que impiden la derivación de partículas no filtradas en la carcasa.",
        },
        {
          title: "Múltiples Capas Filtrantes",
          text: "Combinaciones de malla cuadrada, tejido holandés o láminas sinterizadas según la viscosidad del proceso.",
        },
        {
          title: "Extracción Ergonómica",
          text: "Asas basculantes y barras transversales para retirar la cesta con rapidez durante los ciclos de mantenimiento.",
        },
      ],
    },
    related: {
      heading: "Componentes Filtrantes Relacionados",
      intro: "Explore otros elementos filtrantes de acero inoxidable para clarificación y retención mecánica.",
      cards: [
        {
          title: "Centro de Componentes",
          text: "Gama completa de discos, cilindros, cartuchos, paquetes y conjuntos filtrantes soldados.",
          to: "/FilterComponentsPage",
          image: BASKET_IMAGES.parent,
          badge: "Categoría Principal",
          linkLabel: "Ver Categoría",
        },
        {
          title: "Discos Filtrantes",
          text: "Discos troquelados de una o varias capas y tiras continuas de malla cortada con precisión.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: BASKET_IMAGES.discs,
          badge: "Componente Hermano",
          linkLabel: "Ver Discos",
        },
        {
          title: "Tubos y Cilindros",
          text: "Cilindros de malla de alambre con soldadura longitudinal continua y tubos de soporte.",
          to: "/FilterTubesAndCylindersPage",
          image: BASKET_IMAGES.tubes,
          badge: "Componente Hermano",
          linkLabel: "Ver Tubos",
        },
        {
          title: "Cartuchos Filtrantes",
          text: "Cartuchos industriales cilíndricos y plisados para alta capacidad de retención.",
          to: "/FilterCartridgesPage",
          image: BASKET_IMAGES.cartridges,
          badge: "Componente Hermano",
          linkLabel: "Ver Cartuchos",
        },
        {
          title: "Paquetes de Mallas",
          text: "Packs multicapa soldados por puntos o con marco perimetral para extrusión de polímeros.",
          to: "/ScreenPacksPage",
          image: BASKET_IMAGES.screenPacks,
          badge: "Componente Hermano",
          linkLabel: "Ver Paquetes",
        },
      ],
    },
    cta: {
      id: "cta-filter-baskets",
      eyebrow: "Fabricación a Medida",
      heading: "¿Requiere Medidas Especiales para su Carcasa o Prensa?",
      lead: "Envíenos las cotas de su equipo, el grado de retención requerido y la configuración de brida para cotizar.",
      secondaryLabel: "Contactar a Ingeniería",
      secondaryTo: "/ContactPage",
      note: "Aceptamos planos en formatos DWG, DXF o PDF para estudio técnico.",
    },
  },
  de: {
    breadcrumbKey: "FilterBasketsAndPlates",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Filterkomponenten",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Filterkörbe und Filterplatten",
      lead: "Präzisionsgefertigte Siebkörbe und steife Filterplatten aus Edelstahl für die großvolumige Flüssigkeitsklärung, Rohrleitungsfiltration und Druckblattfilter. Aufgebaut aus schwerem Drahtgewebe, Sintermetallplatten und verstärkten Lochblech-Stützkörpern.",
      secondaryCta: {
        label: "Übersicht Filterkomponenten",
        to: "/FilterComponentsPage",
      },
      image: BASKET_IMAGES.hero,
      cardNote: "Verbund aus Lochblech und Drahtgewebe",
      cardLink: {
        label: "Spezifikationen anzeigen",
        to: "#specifications",
      },
      specs: [
        { label: "Maschenweite", value: "1 µm – 25 mm" },
        { label: "Plattenformate", value: "Bis 2.500 × 1.600 mm" },
        { label: "Stützkörper", value: "Lochblech 304 / 316" },
        { label: "Standardlegierungen", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Strukturelle Steifigkeit & Klärprozesse",
      heading: "Konstruiert für kontinuierliche Flüssigkeitsklärung und Blattfiltration",
      paragraphs: [
        "Filterkörbe und flache Filterplatten sind unverzichtbare Funktionselemente in Schmutzfängern, Klärbehältern, Druckblattfiltern und Filterpressen. Jede Einheit kombiniert präzise Drahtgewebelagen mit massiven Lochblechträgern, um Differenzdrücken ohne Verformung standzuhalten.",
        "Die Ausführungen sind auf kontinuierliche Trennung von Feststoffen und Schlammentwässerung ausgelegt. Wir fertigen Korbeinsätze mit Schrägflansch, flachem oder gewölbtem Boden, CNC-gefertigten Dichtungsrändern und stabilen Hebegriffen.",
        "Filterplatten und Filterblätter werden mit durchgeschweißten Vollrändern, U-Profilfassungen oder Schraubflanschen gefertigt. Mehrere Lagen aus Quadratmasche, Tressengewebe oder Sintermetall verbinden hohe Durchflussraten mit exakter Partikelrückhaltung.",
      ],
      keyPoints: [
        "Robuster Lochblech-Stützkörper verhindert Verformungen bei mechanischer Druckbelastung.",
        "Geometrie nach Behälterzeichnung mit Schrägflansch, Auflagering und Klappbügel.",
        "Flache Filterplatten lieferbar mit schweißdichten Rändern, Schraubflanschen und Dichtungsnuten.",
      ],
      image: BASKET_IMAGES.overview,
    },
    specTable: {
      heading: "Fertigungsparameter & Konstruktionsmerkmale",
      intro: "Abmessungsbereiche und Bauformen für gefertigte Filterkörbe und starre Filterplatten.",
      columns: [
        { key: "parameter", label: "Parameter / Bauteil", align: "left" },
        { key: "filterBaskets", label: "Filterkörbe", align: "left" },
        { key: "filterPlates", label: "Filterplatten & Blätter", align: "left" },
        { key: "standardOptions", label: "Standardoptionen", align: "left" },
      ],
      rows: [
        {
          parameter: "Filterfeinheit",
          filterBaskets: "20 µm – 25 mm",
          filterPlates: "1 µm – 500 µm",
          standardOptions: "Tressengewebe, Quadratmasche, Sinterplatte",
        },
        {
          parameter: "Edelstahllegierungen",
          filterBaskets: "AISI 304, AISI 316, 316L",
          filterPlates: "AISI 304, AISI 316, 316L",
          standardOptions: "Werksprüfzeugnis (MTR) bei Bestellung lieferbar",
        },
        {
          parameter: "Stützstruktur",
          filterBaskets: "Lochblech (versetzte Rundlochung)",
          filterPlates: "Lochblechkern / Drainagerost",
          standardOptions: "1,0 mm bis 4,0 mm Blechstärke",
        },
        {
          parameter: "Maximale Maße",
          filterBaskets: "Durchmesser bis 900 mm, Tiefe bis 1.500 mm",
          filterPlates: "Plattenformat bis 2.500 × 1.600 mm",
          standardOptions: "Fertigung exakt nach technischer Zeichnung",
        },
        {
          parameter: "Rand- und Flanschausführung",
          filterBaskets: "Gedrehter Flanschrand, Drahtringfassung",
          filterPlates: "WIG-verschweißter Rand, U-Einfassung",
          standardOptions: "O-Ring-Nuten, Klappgriffe, Anschweißlaschen",
        },
      ],
      caption: "Angaben stellen typische Fertigungsbereiche dar. Toleranzen richten sich nach Konstruktionszeichnung.",
    },
    features: {
      heading: "Konstruktionsvorteile & Montage",
      intro: "Hohe mechanische Stabilität für anspruchsvolle Filtrationszyklen und einfache Instandhaltung.",
      items: [
        {
          title: "Verstärkter Lochblechkern",
          text: "Dickwandige Lochblechzylinder absorbieren Strömungskräfte und verhindern das Kollabieren feiner Gewebelagen.",
        },
        {
          title: "Passgenaue Randabdichtung",
          text: "Präzise gefertigte Flanschkragen und fugenlos verschweißte Ränder verhindern Bypass um das Gehäuse.",
        },
        {
          title: "Vielfältige Gewebekombinationen",
          text: "Ein-, zwei- oder mehrlagige Gewebeaufbauten zur optimalen Abstimmung auf Viskosität und Schmutzfracht.",
        },
        {
          title: "Ergonomische Handhabung",
          text: "Stabile Hebebügel oder Querträger ermöglichen schnelles Entnehmen zur manuellen Reinigung.",
        },
      ],
    },
    related: {
      heading: "Verwandte Filterkomponenten & Hauptkategorie",
      intro: "Entdecken Sie weitere einbaufertige Filterbaugruppen aus Edelstahl für industrielle Anwendungen.",
      cards: [
        {
          title: "Kategorie Filterkomponenten",
          text: "Komplette Übersicht über Filterscheiben, Zylinder, Kerzen, Siebpakete und Körbe.",
          to: "/FilterComponentsPage",
          image: BASKET_IMAGES.parent,
          badge: "Übergeordnete Kategorie",
          linkLabel: "Übersicht ansehen",
        },
        {
          title: "Filterscheiben & Spaltbänder",
          text: "Ein- und mehrlagige gestanzte Ronden und präzise abgelängte Gewebestreifen.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: BASKET_IMAGES.discs,
          badge: "Komponente",
          linkLabel: "Filterscheiben ansehen",
        },
        {
          title: "Filterrohre & Zylinder",
          text: "Längsnahtgeschweißte und spiralförmig gerollte Drahtgewebezylinder.",
          to: "/FilterTubesAndCylindersPage",
          image: BASKET_IMAGES.tubes,
          badge: "Komponente",
          linkLabel: "Filterrohre ansehen",
        },
        {
          title: "Filterkerzen",
          text: "Plissierte und zylindrische Filterelemente für hohe Schmutzaufnahmekapazität.",
          to: "/FilterCartridgesPage",
          image: BASKET_IMAGES.cartridges,
          badge: "Komponente",
          linkLabel: "Filterkerzen ansehen",
        },
        {
          title: "Siebpakete",
          text: "Punktgeschweißte oder gefasste Mehrlagensiebe für Polymer- und Extrusionsfiltration.",
          to: "/ScreenPacksPage",
          image: BASKET_IMAGES.screenPacks,
          badge: "Komponente",
          linkLabel: "Siebpakete ansehen",
        },
      ],
    },
    cta: {
      id: "cta-filter-baskets",
      eyebrow: "Sonderanfertigung nach Maß",
      heading: "Benötigen Sie Sondermaße für Ihren Druckbehälter oder Ihre Filterpresse?",
      lead: "Senden Sie uns Behälterabmessungen, Maschenweite und Randdetails für ein individuelles Fertigungsangebot.",
      secondaryLabel: "Technik kontaktieren",
      secondaryTo: "/ContactPage",
      note: "Zeichnungen als DWG, DXF oder PDF werden zur Prüfung akzeptiert.",
    },
  },
  fr: {
    breadcrumbKey: "FilterBasketsAndPlates",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Composants de Filtration",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Paniers et Plateaux Filtrants",
      lead: "Paniers filtrants et plateaux rigides en acier inoxydable pour la clarification liquide à grand volume, la filtration en ligne et les filtres sous pression. Réalisés en toiles métalliques calandrées, plaques frittées et noyaux de support en tôle perforée.",
      secondaryCta: {
        label: "Aperçu des Composants",
        to: "/FilterComponentsPage",
      },
      image: BASKET_IMAGES.hero,
      cardNote: "Structure composite tôle perforée et toile",
      cardLink: {
        label: "Voir les Spécifications",
        to: "#specifications",
      },
      specs: [
        { label: "Seuil de Filtration", value: "1 µm – 25 mm" },
        { label: "Dimensions Plateaux", value: "Jusqu'à 2 500 × 1 600 mm" },
        { label: "Noyau Support", value: "Tôle perforée 304 / 316" },
        { label: "Nuances d'Acier", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Rigidité Mécanique et Clarification",
      heading: "Conçus pour la Clarification des Fluides et la Filtration sur Feuilles",
      paragraphs: [
        "Les paniers filtrants et plateaux rigides sont des composants majeurs des filtres à panier de tuyauterie, cuves de clarification et filtres-presses industriels. Chaque ensemble associe une couche filtrante de précision à une sous-structure perforée rigide, évitant toute déformation sous fortes charges hydrauliques.",
        "Les formes fabriquées s'adaptent à la séparation continue des solides et à la déshydratation des boues. Nous réalisons des paniers à collerette inclinée, fond plat ou bombé, collets usinés et anses de levage pour une manipulation rapide.",
        "Les plateaux filtrants et feuilles de presse comportent des bordures soudées étanches, des profils sertis en U ou des cadres boulonnés. L'association de toiles carrées, toiles Reps ou plaques laminées frittées garantit un compromis optimal entre débit et finesse de coupure.",
      ],
      keyPoints: [
        "Noyau rigide en tôle perforée résistant aux fortes pressions sans déformation du média.",
        "Géométrie sur mesure selon cuve avec collerette d'appui, bride usinée et poignées de levage.",
        "Plateaux plats disponibles avec bordures étanches soudées et gorges pour joints d'étanchéité.",
      ],
      image: BASKET_IMAGES.overview,
    },
    specTable: {
      heading: "Paramètres de Fabrication et Spécifications",
      intro: "Dimensions usuelles et modes de construction pour paniers filtrants et plateaux rigides.",
      columns: [
        { key: "parameter", label: "Paramètre / Composant", align: "left" },
        { key: "filterBaskets", label: "Paniers Filtrants", align: "left" },
        { key: "filterPlates", label: "Plateaux et Feuilles", align: "left" },
        { key: "standardOptions", label: "Options Disponibles", align: "left" },
      ],
      rows: [
        {
          parameter: "Seuil de Coupure",
          filterBaskets: "20 µm – 25 mm",
          filterPlates: "1 µm – 500 µm",
          standardOptions: "Tissage Reps, toile carrée, plaque frittée",
        },
        {
          parameter: "Nuances d'Alliage",
          filterBaskets: "AISI 304, AISI 316, 316L",
          filterPlates: "AISI 304, AISI 316, 316L",
          standardOptions: "Certificat MTR disponible sur demande",
        },
        {
          parameter: "Structure Porteuse",
          filterBaskets: "Tôle perforée (perforations rondes)",
          filterPlates: "Noyau perforé / grille de drainage",
          standardOptions: "Épaisseur de tôle de 1,0 mm à 4,0 mm",
        },
        {
          parameter: "Dimensions Maximales",
          filterBaskets: "Diamètre jusqu'à 900 mm, hauteur 1 500 mm",
          filterPlates: "Format jusqu'à 2 500 × 1 600 mm",
          standardOptions: "Fabrication selon plan d'encombrement",
        },
        {
          parameter: "Finition des Bords",
          filterBaskets: "Bride tournée, anneau en fil métallique",
          filterPlates: "Bordure soudée TIG, profilé serti en U",
          standardOptions: "Gorge pour joint torique, anses rabattables",
        },
      ],
      caption: "Valeurs représentatives de fabrication. Les tolérances se conforment aux plans contractuels.",
    },
    features: {
      heading: "Atouts Techniques et Robustesse",
      intro: "Ensembles mécano-soudés étudiés pour résister aux cycles industriels et faciliter l'entretien.",
      items: [
        {
          title: "Noyau Support Renforcé",
          text: "Tôle perforée robuste absorbant les poussées hydrauliques, préservant ainsi la toile filtrante fine.",
        },
        {
          title: "Étanchéité Périphérique",
          text: "Collerettes usinées et soudures continues empêchant les fuites ou le passage direct de particules non filtrées.",
        },
        {
          title: "Assemblage Multicouche",
          text: "Superposition de toiles carrées ou de tissages hollandais pour s'adapter à la viscosité du procédé.",
        },
        {
          title: "Manutention Facilitée",
          text: "Anses rabattables ou poignées transversales pour une extraction sans effort lors des phases de nettoyage.",
        },
      ],
    },
    related: {
      heading: "Composants Connexes et Famille Principale",
      intro: "Découvrez notre gamme d'éléments mécano-soudés en acier inoxydable pour vos installations.",
      cards: [
        {
          title: "Gamme Composants de Filtration",
          text: "Aperçu global de nos disques, cylindres, cartouches, packs et ensembles de filtration.",
          to: "/FilterComponentsPage",
          image: BASKET_IMAGES.parent,
          badge: "Catégorie Mère",
          linkLabel: "Voir la Catégorie",
        },
        {
          title: "Disques et Bandes Refendues",
          text: "Disques découpés mono ou multicouches et rubans continus de toile métallique.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: BASKET_IMAGES.discs,
          badge: "Composant Associé",
          linkLabel: "Voir les Disques",
        },
        {
          title: "Tubes et Cylindres Filtrants",
          text: "Cylindres à soudure longitudinale continue et manchons filtrants en toile d'acier inoxydable.",
          to: "/FilterTubesAndCylindersPage",
          image: BASKET_IMAGES.tubes,
          badge: "Composant Associé",
          linkLabel: "Voir les Tubes",
        },
        {
          title: "Cartouches Filtrantes",
          text: "Éléments plissés ou cylindriques assurant une grande capacité de rétention.",
          to: "/FilterCartridgesPage",
          image: BASKET_IMAGES.cartridges,
          badge: "Composant Associé",
          linkLabel: "Voir les Cartouches",
        },
        {
          title: "Packs de Toiles",
          text: "Packs multicouches soudés par points ou bordés pour l'extrusion de matières plastiques.",
          to: "/ScreenPacksPage",
          image: BASKET_IMAGES.screenPacks,
          badge: "Composant Associé",
          linkLabel: "Voir les Packs",
        },
      ],
    },
    cta: {
      id: "cta-filter-baskets",
      eyebrow: "Fabrication Spécifique sur Mesure",
      heading: "Besoin de Cotes Spécifiques pour votre Cuve ou Presse ?",
      lead: "Transmettez-nous vos dimensions, seuils de rétention et plans de brides pour une étude technique.",
      secondaryLabel: "Consulter l'Ingénierie",
      secondaryTo: "/ContactPage",
      note: "Plans acceptés aux formats DWG, DXF ou PDF pour chiffrage.",
    },
  },
  pt: {
    breadcrumbKey: "FilterBasketsAndPlates",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Componentes Filtrantes",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Cestos e Placas Filtrantes",
      lead: "Cestos filtrantes e placas planas em aço inoxidável para clarificação de líquidos de grande vazão, retenção em tubulações e filtros sob pressão. Construídos com telas calandradas pesadas, placas sinterizadas e núcleos de chapa perfurada reforçada.",
      secondaryCta: {
        label: "Visão Geral de Componentes",
        to: "/FilterComponentsPage",
      },
      image: BASKET_IMAGES.hero,
      cardNote: "Composto de chapa perfurada e tela",
      cardLink: {
        label: "Ver Especificações",
        to: "#specifications",
      },
      specs: [
        { label: "Faixa de Abertura", value: "1 µm – 25 mm" },
        { label: "Formatos de Placas", value: "Até 2.500 × 1.600 mm" },
        { label: "Núcleo de Suporte", value: "Perfurado 304 / 316" },
        { label: "Ligas Metálicas", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Rigidez Estrutural e Clarificação",
      heading: "Projetados para Filtragem sob Pressão e Clarificação Contínua",
      paragraphs: [
        "Cestos filtrantes e placas planas são elementos essenciais em filtros de linha, vasos de clarificação, filtros folha de pressão e sistemas de desaguamento. Cada componente integra uma camada de tela de precisão a um núcleo perfurado rígido, evitando deformações sob altas cargas de fluido.",
        "As configurações atendem à retenção contínua de sólidos e recuperação de resíduos em processos industriais. Fabricamos modelos de cestos com bocais inclinados, fundos planos ou abaulados, anéis flangeados usinados e alças de içamento ergonômicas.",
        "Placas e folhas filtrantes dispõem de soldas de vedação contínua, molduras usinadas em CNC ou flanges aparafusados. Diversas combinações de telas—incluindo malha quadrada, tecido holandês ou laminados sinterizados—harmonizam vazão elevada com retenção precisa de partículas.",
      ],
      keyPoints: [
        "Chapa perfurada de suporte robusto suporta altas pressões mecânicas sem deformação da tela.",
        "Geometria sob medida para vasos de processo, incluindo bocais inclinados, colares e alças.",
        "Placas planas rígidas disponíveis com bordas soldadas estanques, furos para flanges e canais de vedação.",
      ],
      image: BASKET_IMAGES.overview,
    },
    specTable: {
      heading: "Parâmetros de Fabricação e Especificações",
      intro: "Dimensões padrão e opções de montagem para cestos e placas filtrantes rígidas.",
      columns: [
        { key: "parameter", label: "Parâmetro / Componente", align: "left" },
        { key: "filterBaskets", label: "Cestos Filtrantes", align: "left" },
        { key: "filterPlates", label: "Placas e Folhas", align: "left" },
        { key: "standardOptions", label: "Opções Padrão", align: "left" },
      ],
      rows: [
        {
          parameter: "Grau de Retenção",
          filterBaskets: "20 µm – 25 mm",
          filterPlates: "1 µm – 500 µm",
          standardOptions: "Tecido holandês, malha quadrada, placa sinterizada",
        },
        {
          parameter: "Ligas de Aço",
          filterBaskets: "AISI 304, AISI 316, 316L",
          filterPlates: "AISI 304, AISI 316, 316L",
          standardOptions: "Certificado MTR disponível mediante pedido",
        },
        {
          parameter: "Substrato de Suporte",
          filterBaskets: "Chapa perfurada (furos redondos alternados)",
          filterPlates: "Núcleo perfurado / grade de drenagem",
          standardOptions: "Espessura de chapa de 1,0 mm a 4,0 mm",
        },
        {
          parameter: "Dimensões Máximas",
          filterBaskets: "Diâmetro até 900 mm, profundidade até 1.500 mm",
          filterPlates: "Até 2.500 × 1.600 mm",
          standardOptions: "Fabricação precisa conforme desenho técnico",
        },
        {
          parameter: "Acabamento de Bordas",
          filterBaskets: "Flange sólido usinado, anel em arame",
          filterPlates: "Solda contínua TIG, moldura perfilada em U",
          standardOptions: "Canais para anéis O-ring, alças articuladas",
        },
      ],
      caption: "Valores representativos da faixa de fabricação. Tolerâncias determinadas por desenhos do pedido.",
    },
    features: {
      heading: "Destaques Construtivos e Montagem",
      intro: "Construção robusta planejada para ciclos severos de operação e facilidade de limpeza.",
      items: [
        {
          title: "Núcleo de Suporte Perfurado",
          text: "Chapa de aço inoxidável espessa absorve o impacto do fluxo, evitando o colapso da camada filtrante fina.",
        },
        {
          title: "Vedação Sem Bypass",
          text: "Colares de flange usinados e soldas de borda contínuas impedem a passagem indesejada de partículas no vaso.",
        },
        {
          title: "Combinação Multicamadas",
          text: "Montagens de camada simples ou múltipla adequadas à viscosidade e às exigências de processo.",
        },
        {
          title: "Manejo Seguro e Ergonômico",
          text: "Alças basculantes e barras transversais facilitam a remoção durante as paradas para lavagem sob pressão.",
        },
      ],
    },
    related: {
      heading: "Componentes Filtrantes Relacionados",
      intro: "Conheça outros elementos mecânicos em aço inoxidável para processos industriais de clarificação e retenção.",
      cards: [
        {
          title: "Centro de Componentes",
          text: "Visão global de discos, tubos, cartuchos, telas montadas e cestos industriais.",
          to: "/FilterComponentsPage",
          image: BASKET_IMAGES.parent,
          badge: "Categoria Principal",
          linkLabel: "Ver Categoria",
        },
        {
          title: "Discos Filtrantes",
          text: "Discos estampados monocamada e multicamadas e rolos de malha cortados com precisão.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: BASKET_IMAGES.discs,
          badge: "Componente Irmão",
          linkLabel: "Ver Discos",
        },
        {
          title: "Tubos e Cilindros Filtrantes",
          text: "Cilindros de tela com solda longitudinal e tubos de suporte para proteção de linhas.",
          to: "/FilterTubesAndCylindersPage",
          image: BASKET_IMAGES.tubes,
          badge: "Componente Irmão",
          linkLabel: "Ver Tubos",
        },
        {
          title: "Cartuchos Filtrantes",
          text: "Elementos industriais plissados e cilíndricos com elevada capacidade de retenção de sólidos.",
          to: "/FilterCartridgesPage",
          image: BASKET_IMAGES.cartridges,
          badge: "Componente Irmão",
          linkLabel: "Ver Cartuchos",
        },
        {
          title: "Packs de Telas",
          text: "Telas multicamadas soldadas por pontos ou com aro metálico para extrusão de polímeros.",
          to: "/ScreenPacksPage",
          image: BASKET_IMAGES.screenPacks,
          badge: "Componente Irmão",
          linkLabel: "Ver Telas",
        },
      ],
    },
    cta: {
      id: "cta-filter-baskets",
      eyebrow: "Fabricação Sob Medida",
      heading: "Precisa de Dimensões Exclusivas para seu Filtro ou Prensa?",
      lead: "Envie as medidas do seu equipamento, retenção exigida e desenhos de flanges para cotação personalizada.",
      secondaryLabel: "Falar com Engenharia",
      secondaryTo: "/ContactPage",
      note: "Recebemos desenhos em formatos DWG, DXF ou PDF para avaliação.",
    },
  },
};
